import { registerPlugin, type PluginListenerHandle } from '@capacitor/core';

/**
 * Rösten i Q: lyssna (tal till text) och läsa upp (text till tal). I iOS-appen används Apples tal via
 * ios/App/App/QVoicePlugin.swift, i Android-appen SpeechRecognizer och TextToSpeech via
 * android/…/QVoicePlugin.java (Web Speech API finns inte i Androids WebView), i webben
 * webkitSpeechRecognition och speechSynthesis där de finns.
 * Båda har samma API (QVoice), så att panelen inte behöver veta vilken som körs. Ingenting här vet
 * om Q-servern; samtalsläget får en respond-funktion som gör frågan.
 */

export type QVoiceLang = 'sv-SE' | 'en-US';
export type PermissionState = 'granted' | 'denied' | 'restricted' | 'prompt';

export interface QVoiceVoice {
  id: string;
  name: string;
  lang: string;
  quality: 'default' | 'enhanced' | 'premium';
}

export interface ListenOptions {
  lang: QVoiceLang;
  /** Tystnad som avslutar lyssningen. Standard 1200 ms. */
  silenceMs?: number;
  /** Längsta lyssning. Standard 30 s. */
  maxMs?: number;
  /** Delresultat medan man pratar. */
  onPartial?: (text: string) => void;
}

export interface ListenResult {
  text: string;
  /** silence, final, stopped, max, replaced, speak eller error */
  reason: string;
}

export interface SpeakOptions {
  text: string;
  lang: QVoiceLang;
  voiceId?: string;
  /** 0–1, där 0.5 är normal takt. */
  rate?: number;
}

export interface QVoiceState {
  listening: boolean;
  speaking: boolean;
}

export interface QVoice {
  kind: 'native' | 'web' | 'none';
  isAvailable(lang: QVoiceLang): Promise<{ recognition: boolean; synthesis: boolean; onDevice: boolean }>;
  requestPermissions(): Promise<{ speech: PermissionState; microphone: PermissionState }>;
  listen(options: ListenOptions): Promise<ListenResult>;
  /** Avslutar lyssningen; listen() löses med det som hörts. */
  stopListening(): Promise<void>;
  getVoices(lang?: QVoiceLang): Promise<QVoiceVoice[]>;
  /** Löses när uppläsningen är klar (finished: true) eller avbruten (false). */
  speak(options: SpeakOptions): Promise<{ finished: boolean }>;
  /** Avbryter uppläsning och lyssning. */
  stop(): Promise<void>;
  onState(cb: (state: QVoiceState) => void): () => void;
}

export const DEFAULT_SILENCE_MS = 1200;
const DEFAULT_MAX_MS = 30000;
/** Hörs ingenting alls slutar lyssningen efter så här lång tid. */
const NO_SPEECH_MS = 6000;

// ---------- Appen (iOS och Android) ----------

interface QVoicePluginApi {
  isAvailable(o: { lang: string }): Promise<{ recognition: boolean; synthesis: boolean; onDevice: boolean }>;
  requestPermissions(): Promise<{ speech: PermissionState; microphone: PermissionState }>;
  startListening(o: { lang: string; silenceMs?: number; maxMs?: number }): Promise<ListenResult>;
  stopListening(): Promise<void>;
  getVoices(o: { lang?: string }): Promise<{ voices: QVoiceVoice[] }>;
  speak(o: SpeakOptions): Promise<{ finished: boolean }>;
  stop(): Promise<void>;
  selfTest(o: { text: string; lang: string; voiceId?: string }): Promise<{ text: string }>;
  addListener(event: 'partial', cb: (e: { text: string; final: boolean }) => void): Promise<PluginListenerHandle>;
  addListener(event: 'listening', cb: (e: { active: boolean }) => void): Promise<PluginListenerHandle>;
  addListener(event: 'speaking', cb: (e: { active: boolean }) => void): Promise<PluginListenerHandle>;
}

const QVoicePlugin = registerPlugin<QVoicePluginApi>('QVoice');

export function isNativeVoiceAvailable(): boolean {
  const cap = (window as any).Capacitor;
  // Webben laddas från qvenari.com, så äldre appbyggen utan pluginet får webbens röst.
  const platform = cap?.getPlatform?.();
  return !!cap?.isNativePlatform?.() && (platform === 'ios' || platform === 'android') && !!cap.isPluginAvailable?.('QVoice');
}

function createNativeVoice(): QVoice & { selfTest(text: string, lang: QVoiceLang, voiceId?: string): Promise<string> } {
  const state: QVoiceState = { listening: false, speaking: false };
  const subscribers = new Set<(s: QVoiceState) => void>();
  const emit = () => subscribers.forEach(cb => cb({ ...state }));
  void QVoicePlugin.addListener('listening', e => { state.listening = e.active; emit(); });
  void QVoicePlugin.addListener('speaking', e => { state.speaking = e.active; emit(); });

  return {
    kind: 'native',
    isAvailable: lang => QVoicePlugin.isAvailable({ lang }),
    requestPermissions: () => QVoicePlugin.requestPermissions(),
    async listen({ lang, silenceMs, maxMs, onPartial }) {
      const handle = onPartial ? await QVoicePlugin.addListener('partial', e => onPartial(e.text)) : null;
      try {
        return await QVoicePlugin.startListening({ lang, silenceMs: silenceMs ?? DEFAULT_SILENCE_MS, maxMs: maxMs ?? DEFAULT_MAX_MS });
      } finally {
        await handle?.remove();
      }
    },
    stopListening: () => QVoicePlugin.stopListening(),
    getVoices: async lang => (await QVoicePlugin.getVoices({ lang })).voices,
    speak: options => QVoicePlugin.speak(options),
    stop: () => QVoicePlugin.stop(),
    onState(cb) { subscribers.add(cb); return () => { subscribers.delete(cb); }; },
    selfTest: async (text, lang, voiceId) => (await QVoicePlugin.selfTest({ text, lang, voiceId })).text,
  };
}

// ---------- Webben ----------

interface WebRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((e: any) => void) | null;
  onerror: ((e: any) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

export interface WebVoiceEnv {
  SpeechRecognition?: new () => WebRecognition;
  speechSynthesis?: SpeechSynthesis;
  SpeechSynthesisUtterance?: typeof SpeechSynthesisUtterance;
}

function webEnv(): WebVoiceEnv {
  const w = window as any;
  return {
    SpeechRecognition: w.SpeechRecognition || w.webkitSpeechRecognition,
    speechSynthesis: w.speechSynthesis,
    SpeechSynthesisUtterance: w.SpeechSynthesisUtterance,
  };
}

/** Webbläsaren laddar röster asynkront; vänta på voiceschanged en kort stund om listan är tom. */
async function loadWebVoices(synth: SpeechSynthesis): Promise<SpeechSynthesisVoice[]> {
  const now = synth.getVoices();
  if (now.length) return now;
  return new Promise(resolve => {
    const done = () => { synth.removeEventListener?.('voiceschanged', done); resolve(synth.getVoices()); };
    synth.addEventListener?.('voiceschanged', done);
    setTimeout(done, 1000);
  });
}

function webVoiceQuality(v: SpeechSynthesisVoice): QVoiceVoice['quality'] {
  const name = v.name.toLowerCase();
  if (name.includes('premium')) return 'premium';
  if (name.includes('enhanced') || name.includes('förbättrad')) return 'enhanced';
  return 'default';
}

const QUALITY_RANK = { premium: 2, enhanced: 1, default: 0 } as const;

export function createWebVoice(env: WebVoiceEnv = webEnv()): QVoice {
  const state: QVoiceState = { listening: false, speaking: false };
  const subscribers = new Set<(s: QVoiceState) => void>();
  const emit = () => subscribers.forEach(cb => cb({ ...state }));
  let finishListening: ((reason: string) => void) | null = null;
  let speakDone: ((finished: boolean) => void) | null = null;

  const voices = async (lang?: QVoiceLang): Promise<QVoiceVoice[]> => {
    if (!env.speechSynthesis) return [];
    const all = await loadWebVoices(env.speechSynthesis);
    const prefix = lang?.slice(0, 2).toLowerCase();
    return all
      .filter(v => !prefix || v.lang.toLowerCase().replace('_', '-').startsWith(prefix))
      .map(v => ({ id: v.voiceURI, name: v.name, lang: v.lang, quality: webVoiceQuality(v) }))
      .sort((a, b) => QUALITY_RANK[b.quality] - QUALITY_RANK[a.quality] || a.name.localeCompare(b.name));
  };

  const cancelSpeech = () => {
    if (env.speechSynthesis?.speaking || env.speechSynthesis?.pending) env.speechSynthesis.cancel();
    speakDone?.(false);
  };

  return {
    kind: env.SpeechRecognition || env.speechSynthesis ? 'web' : 'none',
    async isAvailable() {
      return { recognition: !!env.SpeechRecognition, synthesis: !!env.speechSynthesis, onDevice: false };
    },
    async requestPermissions() {
      // Webbläsaren frågar om mikrofonen själv när lyssningen startar.
      return { speech: env.SpeechRecognition ? 'prompt' : 'denied', microphone: 'prompt' };
    },
    listen({ lang, silenceMs = DEFAULT_SILENCE_MS, maxMs = DEFAULT_MAX_MS, onPartial }) {
      if (!env.SpeechRecognition) return Promise.reject(new Error('Taligenkänning finns inte i den här webbläsaren'));
      cancelSpeech();
      finishListening?.('replaced');

      return new Promise<ListenResult>(resolve => {
        const rec = new env.SpeechRecognition!();
        rec.lang = lang;
        rec.continuous = true;
        rec.interimResults = true;
        let finalText = '';
        let interim = '';
        let silence: ReturnType<typeof setTimeout> | undefined;
        let ended = false;
        const maxTimer = setTimeout(() => finish('max'), maxMs);
        const arm = (ms: number) => { clearTimeout(silence); silence = setTimeout(() => finish('silence'), ms); };

        const finish = (reason: string) => {
          if (ended) return;
          ended = true;
          clearTimeout(silence);
          clearTimeout(maxTimer);
          finishListening = null;
          rec.onresult = rec.onerror = rec.onend = null;
          try { rec.stop(); } catch { /* redan stoppad */ }
          state.listening = false;
          emit();
          resolve({ text: (finalText + interim).trim(), reason });
        };
        finishListening = finish;

        rec.onresult = (e: any) => {
          // results innehåller hela lyssningen hittills, så texten byggs om från början varje gång.
          finalText = '';
          interim = '';
          for (let i = 0; i < e.results.length; i++) {
            const r = e.results[i];
            if (r.isFinal) finalText += r[0].transcript;
            else interim += r[0].transcript;
          }
          onPartial?.((finalText + interim).trim());
          arm(silenceMs);
        };
        rec.onerror = (e: any) => finish(e?.error === 'aborted' ? 'stopped' : 'error');
        rec.onend = () => finish('final');

        rec.start();
        state.listening = true;
        emit();
        arm(Math.max(silenceMs, NO_SPEECH_MS));
      });
    },
    async stopListening() { finishListening?.('stopped'); },
    getVoices: voices,
    async speak({ text, lang, voiceId, rate }) {
      const synth = env.speechSynthesis;
      if (!synth || !env.SpeechSynthesisUtterance) return { finished: false };
      finishListening?.('speak');
      cancelSpeech();
      const all = await loadWebVoices(synth);
      const utterance = new env.SpeechSynthesisUtterance(text);
      utterance.lang = lang;
      const chosen = (voiceId && all.find(v => v.voiceURI === voiceId))
        || all.find(v => v.lang.replace('_', '-') === lang && webVoiceQuality(v) !== 'default')
        || all.find(v => v.lang.replace('_', '-') === lang);
      if (chosen) utterance.voice = chosen;
      // Webbens skala är 0.1–10 med 1 som normal; 0.5 i vårt API motsvarar 1.
      if (rate !== undefined) utterance.rate = Math.max(0.5, Math.min(2, rate * 2));

      return new Promise(resolve => {
        const done = (finished: boolean) => {
          if (speakDone !== done) return;
          speakDone = null;
          state.speaking = false;
          emit();
          resolve({ finished });
        };
        speakDone = done;
        utterance.onend = () => done(true);
        utterance.onerror = () => done(false);
        state.speaking = true;
        emit();
        synth.speak(utterance);
      });
    },
    async stop() {
      cancelSpeech();
      finishListening?.('stopped');
    },
    onState(cb) { subscribers.add(cb); return () => { subscribers.delete(cb); }; },
  };
}

// ---------- Val av motor ----------

let current: ReturnType<typeof createNativeVoice> | QVoice | null = null;

/** Appens röst om pluginet finns, annars webbens. */
/** Lyssnar, och försöker en gång till om iOS svarar att mikrofonen är upptagen (NO_INPUT): under jakt kan WebKit
 *  hålla den en stund efter att jaktens samtal och transkription släppt den (QB1). */
export async function listenWithRetry(voice: Pick<QVoice, 'listen'>, options: ListenOptions, retryMs = 600): Promise<ListenResult> {
  try {
    return await voice.listen(options);
  } catch (err) {
    if ((err as { code?: string })?.code !== 'NO_INPUT') throw err;
    await new Promise(r => setTimeout(r, retryMs));
    return voice.listen(options);
  }
}

export function getQVoice(): QVoice {
  if (!current) current = isNativeVoiceAvailable() ? createNativeVoice() : createWebVoice();
  return current;
}

/** Bara i appen: läser upp texten till ljud och tolkar det, utan mikrofon (för simulatorn). */
export async function voiceSelfTest(text: string, lang: QVoiceLang, voiceId?: string): Promise<string> {
  const voice = getQVoice() as Partial<ReturnType<typeof createNativeVoice>>;
  if (!voice.selfTest) throw new Error('Självtestet finns bara i appen');
  return voice.selfTest(text, lang, voiceId);
}

// ---------- Språk och samtalsläge ----------

/** Enhetens språk som Q-språk: svenska om enheten är på svenska, annars engelska. */
export function deviceLang(languages: readonly string[] = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : []): QVoiceLang {
  return languages.find(Boolean)?.toLowerCase().startsWith('sv') ? 'sv-SE' : 'en-US';
}

const normalize = (text: string) => text.toLowerCase().replace(/[.,!?;:]/g, ' ').replace(/\s+/g, ' ').trim();

/** "speak English" eller "prata svenska" byter språk. */
export function detectLanguageSwitch(text: string): QVoiceLang | null {
  const t = normalize(text);
  if (/\b(speak|talk|prata|tala) (in )?english\b|\bpå engelska\b|\bin english\b/.test(t)) return 'en-US';
  if (/\b(prata|tala|speak|talk) (på |in )?(svenska|swedish)\b|\bpå svenska\b|\bin swedish\b/.test(t)) return 'sv-SE';
  return null;
}

/** "Tack, det var allt" (eller motsvarande) avslutar samtalsläget. */
export function isEndPhrase(text: string): boolean {
  const t = normalize(text);
  return /^(tack )?det var allt( tack)?$/.test(t)
    || /^(tack )?(hej då|hejdå|avsluta|sluta lyssna)$/.test(t)
    || /^(thanks |thank you )?(that's all|that is all|goodbye|bye|stop listening)( thanks| thank you)?$/.test(t);
}

export interface ConversationOptions {
  lang: QVoiceLang;
  /** Gör frågan och returnerar svaret som ska läsas upp (och språket det är på, om det byttes). */
  respond: (text: string, lang: QVoiceLang) => Promise<string | { text: string; lang?: QVoiceLang }>;
  /** false = tryck och prata: en fråga och ett svar. Standard true. */
  continuous?: boolean;
  voiceId?: (lang: QVoiceLang) => string | undefined;
  silenceMs?: number;
  onPartial?: (text: string) => void;
  onTurn?: (turn: { heard: string; reply: string; lang: QVoiceLang }) => void;
  signal?: AbortSignal;
  /** Skydd mot en loop som aldrig tar slut. */
  maxTurns?: number;
}

/**
 * Samtalsläge: lyssna, fråga, läs upp svaret och lyssna igen när uppläsningen är klar. Slutar när
 * man säger "tack, det var allt", när inget hörs, när uppläsningen avbryts eller när signal avbryts.
 * Returnerar varför det slutade.
 */
export async function runConversation(voice: QVoice, options: ConversationOptions): Promise<'end-phrase' | 'silence' | 'interrupted' | 'aborted' | 'max-turns' | 'single'> {
  let lang = options.lang;
  const maxTurns = options.maxTurns ?? 50;
  const onAbort = () => { void voice.stop(); };
  options.signal?.addEventListener('abort', onAbort);
  try {
    for (let turn = 0; turn < maxTurns; turn++) {
      if (options.signal?.aborted) return 'aborted';
      const heard = await voice.listen({ lang, silenceMs: options.silenceMs, onPartial: options.onPartial });
      if (options.signal?.aborted) return 'aborted';
      const text = heard.text.trim();
      if (!text) return 'silence';
      if (isEndPhrase(text)) return 'end-phrase';
      lang = detectLanguageSwitch(text) ?? lang;

      const answer = await options.respond(text, lang);
      const reply = typeof answer === 'string' ? answer : answer.text;
      if (typeof answer !== 'string' && answer.lang) lang = answer.lang;
      options.onTurn?.({ heard: text, reply, lang });
      if (options.signal?.aborted) return 'aborted';

      const spoken = await voice.speak({ text: reply, lang, voiceId: options.voiceId?.(lang) });
      if (options.continuous === false) return 'single';
      if (!spoken.finished) return options.signal?.aborted ? 'aborted' : 'interrupted';
    }
    return 'max-turns';
  } finally {
    options.signal?.removeEventListener('abort', onAbort);
  }
}
